export enum Pages {
  Sell = '/sell',
  SellCheckRegistration = '/sell/check-registration/:vrm',
  SellManualLookup = '/sell/manual-lookup/:vrm',
  SellCar = '/sell/car',
  SellDetails = '/sell/details',
  SellValuationView = '/sell/valuation/view',
  SellValuationUpdate = '/sell/valuation/update',
  SellAppointmentVerify = '/sell/appointment/verify',
  SellAppointmentBooked = '/sell/appointment/booked',
}

export enum Stage {
  AppointmentConfirmed = 'AppointmentConfirmed',
  AppointmentReschedule = 'AppointmentReschedule',
  AppointmentManage = 'AppointmentManage',
  AppointmentDetails = 'AppointmentDetails',
  AppointmentCancellation = 'AppointmentCancellation',
  AppointmentVerify = 'AppointmentVerify',
  VehicleManualLookup = 'VehicleManualLookup',
  VehicleQuestions = 'VehicleQuestions',
  VehicleMulticar = 'VehicleMulticar',
  VehicleRegistrationCheck = 'VehicleRegistrationCheck',
  Redirect = 'Redirect',
  ValuationRenew = 'ValuationRenew',
  ValuationUpdate = 'ValuationUpdate',
  Valuation = 'Valuation',
}

export enum QuestionControlType {
  Unknown = 0,
  TextEntry = 1,
  LocationTextEntry = 2,
  ListRadio = 3,
  BoolRadio = 4,
  InlineBoolRadio = 5,
  ListDropdown = 6,
}

export enum JourneyPageType {
  VehicleDetails = 0,
  PersonalDetails = 1,
  OptionalExtras = 2,
  IdentifyVehicle = 3,
  ManualLookup = 4,
}

export interface VehicleInformation {
  quoteId: number
  quoteGuid: string
  garageGuid: null
  visitorIsBlocked: boolean
  vehicleRecentlyPurchased: boolean
  validationErrors: any[]
  hashedVrm: string
  journey: Journey
  vehicleLabelValueLists: VehicleLabelValueListsItemItem[][]
  availableOptionalExtras: any[]
  appointmentStatus: AppointmentStatus
}

export interface ProcessedVehicleInformation {
  vehicleInformation: VehicleInformation
  stage: Stage
}

export interface VehicleStatus {
  isRecentlyPurchased: boolean
  hasAppointment: boolean
  isNotFoundOrManualLookup: boolean
  isMulticar: boolean
}

export interface Journey {
  journeyVariant: string
  journeyPages: JourneyPagesItem[]
}

export interface JourneyPagesItem {
  pageNumber: number
  hasOptionalExtras: boolean
  optionalExtraQuestionControlType: number
  journeyPageType: number
  pageDisplayContent: PageDisplayContentItem[]
  pageQuestions: PageQuestionsItem[]
}

export interface PageDisplayContentItem {
  languageCode: string
  pageTitle: string
  pageDescription: string
}

export interface PageQuestionsItem {
  questionParameterId: number
  questionParameterName: string
  questionOrderPosition: null
  answerDataType: AnswerDataType
  validationRegEx: string | null
  isOptionalExtra: boolean
  mobileInputType: MobileInputType
  currentAnswerValue: string
  isTooltipAutoDisplayed: boolean
  isRequired: boolean
  isHidden: boolean
  dependentQuestionParameterId: null
  questionControlType: number
  questionsDisplayContent: QuestionsDisplayContentItem[]
  canBeAssumed: boolean
  assumedValue: string
}

export enum MobileInputType {
  Text = 0,
  Email = 1,
  Tel = 2,
}

export enum AnswerDataType {
  String = 0,
  Bool = 1,
  Int = 2,
  Number = 3,
}

export interface QuestionsDisplayContentItem {
  languageCode: string
  questionText: string
  questionExplanation: null | string
  multipleChoiceAnswerOptions: MultipleChoiceAnswerOptionsItem[]
  hasDynamicChoiceAnswerOptions?: boolean
  assumptionText: null
  formFieldLabel: string
  toolTipText: string
  answerRequiredErrorText: string | null
  answerFormatErrorText: string | null
}

export interface MultipleChoiceAnswerOptionsItem {
  optionDisplayText: string
  optionValue: number | string
}

export interface VehicleLabelValueListsItemItem {
  label: string | null
  value: string
  sortOrder: null | number
}

export interface AppointmentStatus {
  hasAppointment: boolean
  hasPastAppointmentToday: boolean
  hasFutureAppointmentToday: boolean
  hasFutureAppointmentAfterToday: boolean
  appointmentSiteId: null
}

/**
 * Valuation
 */

export interface DamageItem {
  damageId: number
  valuationId: number
  valuationGuid: string
  quoteId: number
  quoteGuid: string
  vehicleZoneId: number
  vehicleZoneName: string
  componentId: number
  componentName: string
  faultId: number
  faultName: string
  lastUpdated: string
}

export interface ValuationResponse {
  valuationId: number
  valuationGuid: string
  quoteId: number
  quoteGuid: string
  valuationCount: number
  createdDate: string
  expiredDate: null
  vehicleNumber: number
  vehicleCategory: string
  vehiclePriceOffered: number
  vatAmount: number
  adminFee: number
  valueBand: number
  vrm: string
  hashedVrm: string
  guideBookId: number
  bookId: number
  customerPostcode: string
  vehicleRecentlyPurchased: boolean
  visitorIsBlocked: boolean
  vehicleCannotBeValued: boolean
  requiresManualValuation: null
  assumptions: AssumptionsItem[]
  damages: DamageItem[]
  journey: Journey
  validationErrors: any[]
  appointmentStatus: AppointmentStatus
}

export interface PersonalInformationResponse extends ValuationResponse {
  vehicleAttributes: VehicleAttributesItem[]
}

export interface VehicleAttributesItem {
  parameterId: number
  parameterName: null
  parameterValue: string | null
}

export interface AssumptionsItem {
  parameterId: number
  languageCd: string
  assumptionText: string
}

/**
 * Site
 */
export interface Site {
  siteId: number
  name: string
  shortSiteName: string
  emailAddress: string
  directPhoneNumber: string
  address1: string | null
  address2: string | null
  address3: string | null
  address4: string | null
  postcode: string | null
  locationLng: number
  locationLat: number
  distanceInMeters: null
  metaTitle: string
  metaDescription: string
  metaKeywords: string
  metaPlaceName: string
  metaRegion: string
  introductionParagraph: string
  locationDescription: string
  pageName: string
  rewriteUrl: string
  mapUrl: string
  nearestTrain: string
  nearestBus: string
  buyerNames: any[]
  futureOpenCloseTimesForWeek: OpenCloseTime[]
}

export interface OpenCloseTime {
  dayId: number
  startTime: string
  endTime: string
}

/**
 * Appointment
 */
export interface BookResponse {
  appointmentId: number
  appointmentGuid: string
  quoteId: number
  quoteGuid: string
  valuationId: number
  valuationGuid: string
  siteId: number
  date: string
  startTime: string
  endTime: string
  vrm: string | null
  guideBookId: number
  customerEmailAddress: string | null
  customerPostcode: string | null
  vrmAlreadyHasAppointment: boolean
  isCancelled: boolean
  validationErrors: string[]
}
